import React, { useState } from 'react';
import { Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    amount: '',
    description: '',
    telegram: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        navigate('/successful');
      } else {
        // Duplicate phone number
        toast.warning(response.data.message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error. Please try again.';
      toast.error(errorMessage);
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openTelegram = () => {
    window.open('https://t.me/Capitara', '_blank');
  };

  return (
    <section id="contact" className="contact-section">
      <div className="section-container">
        <div className="section-header centered">
          <h2 className="section-title">{t.contactTitle}</h2>
          <p className="section-subtitle">{t.contactSubtitle}</p>
        </div>

        <div className="contact-content">
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">{t.yourName} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={t.yourNamePlaceholder}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">{t.phone} *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="+1 (XXX) XXX-XX-XX"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">{t.email} *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="telegram" className="form-label">{t.telegram}</label>
                  <input
                    type="text"
                    id="telegram"
                    name="telegram"
                    value={formData.telegram}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="@username"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="amount" className="form-label">{t.lossAmount}</label>
                <input
                  type="text"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="$10,000"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">{t.describeYourSituation} *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="form-textarea"
                  placeholder={t.describePlaceholder}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary btn-large btn-full"
              >
                {isSubmitting ? t.sending : t.sendRequest}
                <Send size={20} />
              </button>
            </form>
          </div>

          <div className="contact-telegram">
            <div className="telegram-card">
              <MessageCircle size={64} className="telegram-icon" />
              <h3 className="telegram-title">{t.contactViaTelegram}</h3>
              <p className="telegram-description">{t.telegramDesc}</p>
              <button onClick={openTelegram} className="btn-secondary btn-large btn-full">
                {t.openTelegram}
                <MessageCircle size={20} />
              </button>
              <div className="telegram-contact">Telegram @Capitara</div>
            </div>

            <div className="contact-info">
              <div className="info-item">
                <h4 className="info-title">{t.email}</h4>
                <p className="info-text">CapitaraFinancial@gmail.com</p>
              </div>
              <div className="info-item">
                <h4 className="info-title">{t.workSchedule}</h4>
                <p className="info-text">{t.internationalWork}</p>
              </div>
              <div className="info-item">
                <h4 className="info-title">{t.consultation}</h4>
                <p className="info-text">{t.firstConsultationFree}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
